/**
 * @changelog
 *
 * 2026-09-09 (0.1.0) (jhoward)
 *   - Add src\helpers\getReadableDate.ts to turn an Engage timestamp into the date as a person would write it, plus
 *     how long ago it was.
 */

import { getDateFromEngage } from '@/helpers/getdateFromEngage';

export interface ReadableDate {
  date: Date | null;
  //'Sat, Sep 6' this year, 'Sat, Sep 6, 2025' outside it. Falls back to the raw string when the date cannot be read.
  absolute: string;
  //'Today', 'Yesterday', '3 days ago', 'Last month', '2 years ago'. Empty when the date cannot be read.
  relative: string;
  //For <time datetime>. Empty when the date cannot be read.
  machine: string;
}

//Compared at day granularity: a person thinks of a tournament as happening on a day, not at a minute.
function getWholeDaysFromToday(date: Date): number {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);

  const startOfTarget = new Date(date.getTime());
  startOfTarget.setHours(0, 0, 0, 0);

  return Math.round((startOfTarget.getTime() - startOfToday.getTime()) / 86400000);
}

/**
 * Picks the unit a person would actually use for the distance, then lets Intl phrase it. numeric: 'auto' is what
 * produces 'Yesterday' and 'Last month' rather than '1 day ago' and '1 month ago'.
 *
 * Months and years are counted off the calendar rather than by dividing days, so a date in the previous month always
 * reads as a month away however few days ago it fell. The months branch is skipped when the calendar puts both dates
 * in the same month, since 'this month' says less about four weeks ago than '4 weeks ago' does.
 */
function getRelativePhrase(date: Date): string {
  const days = getWholeDaysFromToday(date);
  const now = new Date();
  const months = (date.getFullYear() - now.getFullYear()) * 12 + (date.getMonth() - now.getMonth());
  const format = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

  let phrase;
  if (Math.abs(days) < 7) {
    phrase = format.format(days, 'day');
  } else if (Math.abs(days) < 28 || 0 === months) {
    phrase = format.format(Math.round(days / 7), 'week');
  } else if (Math.abs(months) < 12) {
    phrase = format.format(months, 'month');
  } else {
    phrase = format.format(Math.round(months / 12), 'year');
  }

  //Intl lower-cases 'yesterday' and 'last week' for use mid-sentence; these stand on their own.
  return phrase.charAt(0).toUpperCase() + phrase.slice(1);
}

export default function getReadableDate(dateTime: string): ReadableDate {
  const date = getDateFromEngage(dateTime);
  if (!date) {
    return { date: null, absolute: dateTime, relative: '', machine: '' };
  }

  //The year is dropped within the current year: it is the same on every row, and the weekday is the more useful
  //thing to spend the space on for something that happened on a particular day.
  const absolute = new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() === new Date().getFullYear() ? undefined : 'numeric',
  }).format(date);

  return {
    date,
    absolute,
    relative: getRelativePhrase(date),
    machine: date.toISOString(),
  };
}
